<template>
  <div
    v-if="model"
    class="modal fade show"
    tabindex="-1"
    style="display: block; background-color: rgba(0, 0, 0, 0.5)"
    @click.self="model = false"
  >
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Add New EAC Price</h5>
          <button
            type="button"
            class="btn-close"
            @click="model = false"
          ></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="handleSubmit">
            <div class="mb-3">
              <label for="priceVintage" class="form-label">Vintage *</label>
              <input
                id="priceVintage"
                v-model.number="formData.vintage"
                type="number"
                class="form-control"
                required
                min="2020"
                max="2050"
              />
            </div>
            <div class="mb-3">
              <label for="priceBid" class="form-label"
                >Bid Price (€/MWh) *</label
              >
              <input
                id="priceBid"
                v-model.number="formData.bid"
                type="number"
                step="0.0001"
                class="form-control"
                required
                min="0"
              />
            </div>
            <div class="mb-3">
              <label for="priceAsk" class="form-label"
                >Ask Price (€/MWh) *</label
              >
              <input
                id="priceAsk"
                v-model.number="formData.ask"
                type="number"
                step="0.0001"
                class="form-control"
                required
                min="0"
              />
            </div>
            <div class="mb-3">
              <label for="priceSource" class="form-label">Source</label>
              <select
                id="priceSource"
                v-model="formData.source"
                class="form-select"
              >
                <option value="provider1">provider1</option>
              </select>
            </div>
            <div class="mb-3">
              <label for="priceName" class="form-label">Product Name *</label>
              <input
                id="priceName"
                v-model="formData.name"
                type="text"
                class="form-control"
                required
                maxlength="255"
                placeholder="e.g., GO - Nordic hydro"
              />
            </div>
            <div class="mb-3">
              <label for="priceDate" class="form-label">Date</label>
              <input
                id="priceDate"
                v-model="formData.date"
                type="date"
                class="form-control"
              />
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            @click="model = false"
          >
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-primary"
            @click="handleSubmit"
            :disabled="loading"
          >
            <span
              v-if="loading"
              class="spinner-border spinner-border-sm me-2"
            ></span>
            Add Price
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { ref, watch } from "vue";

interface NewPrice {
  vintage: string;
  bid: number;
  ask: number;
  source: string;
  name: string;
  date: string;
}

const emit = defineEmits<{
  "price-added": [];
}>();

const model = defineModel<boolean>({ required: true });

// Global handlers
const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();

// State
const loading = ref(false);
const formData = ref<NewPrice>({
  vintage: String(new Date().getFullYear()),
  bid: 0,
  ask: 0,
  source: "provider1",
  name: "GO - Nordic hydro",
  date: new Date().toISOString().split("T")[0],
});

// Reset form when modal closes
watch(model, (isOpen) => {
  if (!isOpen) {
    resetForm();
  }
});

const resetForm = () => {
  formData.value = {
    vintage: String(new Date().getFullYear()),
    bid: 0,
    ask: 0,
    source: "provider1",
    name: "GO - Nordic hydro",
    date: new Date().toISOString().split("T")[0],
  };
};

const handleSubmit = async () => {
  if (formData.value.ask <= formData.value.bid) {
    globalErrorHandler(new Error("Ask price must be greater than bid price"));
    return;
  }

  loading.value = true;
  try {
    await goSolidApi.post("/api/admin/eac-pricing/prices", formData.value);
    globalSuccessHandler("Success", "EAC price added successfully");
    model.value = false;
    emit("price-added");
  } catch (error: any) {
    globalErrorHandler(error);
  } finally {
    loading.value = false;
  }
};
</script>
